import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    TextField,
    Typography,
    TableContainer,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Paper,
    CircularProgress,
    TablePagination,
    Backdrop,
    Button,
    Tooltip,
    IconButton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LockResetIcon from '@mui/icons-material/LockReset';
import { useTheme } from '@mui/material/styles';
import { DataTableWithStaticSchemaProps, CustomAction } from './DataTable.types';
import { Logger } from "utils/Logger";
import { handleApiError } from 'utils/ErrorHandler';
import { apiClient } from "utils/ApiClient";
import { ColumnPreferenceManager, ColumnPreference } from 'utils/ColumnPreferenceManager';
import { ColumnSelector } from './ColumnSelector';
import { useSelector } from 'react-redux';
import { selectLabels } from 'state/dictionary/dictionarySlice';

export const DataTableWithStaticSchema = ({
    dataUrl,
    schemaObject,
    columnsToExclude,
    onEdit,
    onDelete,
    columnRenderers,
    refreshTrigger = 0,
    isDeleteDisabled,
    deleteDisabledTooltip,
    customActions,
    customDataProcessor,
    columnsWithDisabledFilter = [],
    dataSetName,
    enableColumnPreferences = false,
    headerRightContent,
    allowedOptionalColumns,
    mandatoryColumns: mandatoryColumnsProp,
    externalSearchQuery,
    hideSearch = false,
    initialSortColumn = null,
    initialSortOrder = 'asc',
}: DataTableWithStaticSchemaProps & {
    dataSetName?: string;
    enableColumnPreferences?: boolean;
    headerRightContent?: React.ReactNode;
    allowedOptionalColumns?: string[];
    mandatoryColumns?: string[];
}) => {
    const defaultMandatoryColumns = mandatoryColumnsProp || ['campaignName', 'campaignType', 'startTimestamp', 'status'];
    const theme = useTheme();
    const labels = useSelector((state: any) => selectLabels(state));
    const [rawData, setRawData] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    // State for sorting
    const [sortColumn, setSortColumn] = useState<string | null>(initialSortColumn ?? null);
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(initialSortOrder);

    // State for global search
    const [searchQuery, setSearchQuery] = useState<string>('');

    // State for column-specific filters
    const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});

    // State for pagination
    const [page, setPage] = useState<number>(0);
    const [rowsPerPage, setRowsPerPage] = useState<number>(10);

    // State for column preferences
    const [columnPreferences, setColumnPreferences] = useState<ColumnPreference>({
        mandatoryColumns: defaultMandatoryColumns,
        optionalColumns: [],
        allColumns: [...defaultMandatoryColumns]
    });

    // Normalize values for search/filter (handles synonyms)
    const normalizeForFilter = (column: string, value: any): string => {
        let v = String(value ?? '').toLowerCase().trim();
        if (column === 'campaignType') {
            // Map synonyms to canonical values used in data
            // email -> mail
            if (v === 'email' || v.includes('email')) v = 'mail';
            // whatsapp business variants -> whatsapp
            if (v === 'whatsapp business' || v === 'whatsappbusiness' || v.includes('whatsapp business')) v = 'whatsapp';
        }
        return v;
    };
    
    // Helpers to support robust date filtering (matches multiple locale formats)
    const isDateColumn = (column: string): boolean => {
        return schemaObject?.paths?.[column]?.instance === 'Date';
    };
    const getFilterableStrings = (column: string, rawValue: any): string[] => {
        // Campaign type normalization as single string
        if (column === 'campaignType') {
            return [normalizeForFilter(column, rawValue)];
        }
        // Status: include raw and human-friendly labels used in UI
        if (column === 'status') {
            const raw = String(rawValue ?? '').toLowerCase();
            const values = [raw];
            if (raw === 'awaiting_processing') {
                values.push('awaiting processing');
            }
            if (raw === 'quota_limit_reached') {
                // Match the UI display text for filtering
                values.push('paused : not enough credits');
                values.push('paused');
                values.push('not enough credits');
            }
            return values;
        }
        // Dates: build multiple locale representations so user input matches what is seen
        if (isDateColumn(column)) {
            try {
                const d = new Date(rawValue);
                if (!isNaN(d.getTime())) {
                    const yyyy = d.getFullYear();
                    const mm = String(d.getMonth() + 1).padStart(2, '0');
                    const dd = String(d.getDate()).padStart(2, '0');
                    const m = String(d.getMonth() + 1);
                    const day = String(d.getDate());

                    const gbFull = d.toLocaleString('en-GB', {
                        year: 'numeric', month: '2-digit', day: '2-digit',
                        hour: '2-digit', minute: '2-digit', second: '2-digit'
                    }).toLowerCase();
                    const gbFullUnpadded = d.toLocaleString('en-GB', {
                        year: 'numeric', month: 'numeric', day: 'numeric',
                        hour: '2-digit', minute: '2-digit', second: '2-digit'
                    }).toLowerCase();
                    const gbFullTz = d.toLocaleString('en-GB', {
                        year: 'numeric', month: '2-digit', day: '2-digit',
                        hour: '2-digit', minute: '2-digit', second: '2-digit',
                        timeZoneName: 'short'
                    }).toLowerCase();
                    const gbDate = d.toLocaleDateString('en-GB', {
                        year: 'numeric', month: '2-digit', day: '2-digit'
                    }).toLowerCase();
                    const gbDateUnpadded = d.toLocaleDateString('en-GB', {
                        year: 'numeric', month: 'numeric', day: 'numeric'
                    }).toLowerCase();
                    // Explicit GB combinations (DD/MM/YYYY) with/without leading zeros
                    const gbDDMMYYYY = `${dd}/${mm}/${yyyy}`.toLowerCase();
                    const gbDMMYYYY = `${day}/${mm}/${yyyy}`.toLowerCase();
                    const gbDDMYYYY = `${dd}/${m}/${yyyy}`.toLowerCase();
                    const gbDMYYYY = `${day}/${m}/${yyyy}`.toLowerCase();

                    const usFull = d.toLocaleString('en-US', {
                        year: 'numeric', month: '2-digit', day: '2-digit',
                        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true
                    }).toLowerCase();
                    const usFullUnpadded = d.toLocaleString('en-US', {
                        year: 'numeric', month: 'numeric', day: 'numeric',
                        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true
                    }).toLowerCase();
                    const usDate = d.toLocaleDateString('en-US', {
                        year: 'numeric', month: '2-digit', day: '2-digit'
                    }).toLowerCase();
                    const usDateUnpadded = d.toLocaleDateString('en-US', {
                        year: 'numeric', month: 'numeric', day: 'numeric'
                    }).toLowerCase();
                    // Explicit US combinations (MM/DD/YYYY) with/without leading zeros
                    const usMMDDYYYY = `${mm}/${dd}/${yyyy}`.toLowerCase();
                    const usMDDYYYY = `${m}/${dd}/${yyyy}`.toLowerCase();   // e.g., 11/08/2025 -> 11/8/2025
                    const usMMDYYYY = `${mm}/${day}/${yyyy}`.toLowerCase();
                    const usMDYYYY = `${m}/${day}/${yyyy}`.toLowerCase();    // e.g., 11/8/2025

                    const iso = d.toISOString().toLowerCase();
                    return [
                        // GB
                        gbFull, gbFullUnpadded, gbFullTz, gbDate, gbDateUnpadded,
                        gbDDMMYYYY, gbDMMYYYY, gbDDMYYYY, gbDMYYYY,
                        // US
                        usFull, usFullUnpadded, usDate, usDateUnpadded,
                        usMMDDYYYY, usMDDYYYY, usMMDYYYY, usMDYYYY,
                        // ISO
                        iso
                    ];
                }
            } catch (_e) {
                // fall through to default
            }
        }
        // Default: just stringified lower-case
        return [String(rawValue ?? '').toLowerCase()];
    };

    // Effect to load column preferences if enabled
    useEffect(() => {
        if (enableColumnPreferences && dataSetName) {
            const saved = ColumnPreferenceManager.getColumnPreferences(dataSetName);
            Logger.debug("DataTableWithStaticSchema: Loading column preferences for dataset:", dataSetName);
            Logger.debug("DataTableWithStaticSchema: Loaded preferences:", saved);

            // Reconcile saved preferences with the current mandatoryColumns prop so
            // code changes to mandatory columns are always respected (old cookies
            // may contain a different mandatory set).
            const rawOptional = saved.optionalColumns || [];
            const validOptional = rawOptional.filter((col: string) =>
                !defaultMandatoryColumns.includes(col) &&
                !columnsToExclude.includes(col) &&
                (!allowedOptionalColumns || allowedOptionalColumns.includes(col))
            );
            const reconciled: ColumnPreference = {
                mandatoryColumns: defaultMandatoryColumns,
                optionalColumns: validOptional,
                allColumns: Array.from(new Set([...defaultMandatoryColumns, ...validOptional])),
            };

            ColumnPreferenceManager.saveColumnPreferences(dataSetName, reconciled);
            setColumnPreferences(reconciled);
        }
    }, [enableColumnPreferences, dataSetName, defaultMandatoryColumns, allowedOptionalColumns, columnsToExclude]);

    // Helper function to extract endpoint path from full URL
    const getEndpointPath = (url: string): string => {
        try {
            // If it's already a relative path (starts with /), return as is
            if (url.startsWith('/')) {
                return url;
            }
            
            // If it's a full URL, extract the path and query string
            const urlObj = new URL(url);
            // Include both pathname and search (query parameters)
            return urlObj.pathname + urlObj.search;
        } catch (error) {
            // If URL parsing fails, assume it's already a relative path
            return url;
        }
    };

    // Effect to fetch only data (schema is static)
    useEffect(() => {
        const fetchData = async (): Promise<void> => {
            try {
                setLoading(true);

                // Extract endpoint path from full URL to avoid double URL construction
                const endpointPath = getEndpointPath(dataUrl);
                Logger.debug("src/components/DataTable/DataTableWithStaticSchema.tsx : Using data endpoint path:", endpointPath);

                // Fetch data using ApiClient (automatically includes Authorization header)
                const dataResult = await apiClient.get(endpointPath);
                
                // Handle both response formats: { data: [...] } and [...]
                let dataArray: any[];
                if (dataResult && dataResult.data && Array.isArray(dataResult.data)) {
                    dataArray = dataResult.data;
                } else if (Array.isArray(dataResult)) {
                    dataArray = dataResult;
                } else {
                    dataArray = [];
                }
                
                // Apply custom data processor if provided
                if (customDataProcessor) {
                    dataArray = customDataProcessor(dataArray);
                }
                
                setRawData(dataArray);
                Logger.debug("src/components/DataTable/DataTableWithStaticSchema.tsx : Fetched data:", dataArray);

                setLoading(false);

            } catch (err: unknown) {
                const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
                setError(`Failed to load data: ${errorMessage}`);
                Logger.error("src/components/DataTable/DataTableWithStaticSchema.tsx : fetchData: Failed to fetch data:", err);
                setLoading(false);
            }
        };

        fetchData();
    }, [dataUrl, refreshTrigger, customDataProcessor]);

    // Determine which columns to display based on provided schemaObject
    const displayColumns = useMemo((): string[] => {
        if (!schemaObject) return [];
        let columns: string[] = [];

        if (schemaObject.paths) {
            columns = Object.keys(schemaObject.paths);
        } else if (schemaObject.obj) {
            columns = Object.keys(schemaObject.obj);
        } else {
            if (rawData.length > 0) {
                columns = Object.keys(rawData[0]);
            }
        }

        Logger.debug("DataTableWithStaticSchema: displayColumns calculation", {
            schemaObject: !!schemaObject,
            columnsFromSchema: columns,
            rawDataLength: rawData.length,
            enableColumnPreferences,
            dataSetName,
            columnPreferences
        });

        // Filter out excluded columns
        const availableColumns = columns.filter(key => !columnsToExclude.includes(key));

        // If column preferences are enabled, use them to filter columns
        if (enableColumnPreferences && dataSetName) {
            const mandatoryColumns = columnPreferences.mandatoryColumns;
            const optionalColumns = columnPreferences.optionalColumns;
            
            // Always include mandatory columns from the schema, even if no data exists
            const mandatoryColumnsToShow = mandatoryColumns.filter(col => 
                columns.includes(col) && !columnsToExclude.includes(col)
            );
            
            // Filter optional columns to only show those that exist in the schema and aren't excluded
            const optionalColumnsToShow = optionalColumns.filter(col => 
                columns.includes(col) && !columnsToExclude.includes(col)
            );
            
            let result = [...mandatoryColumnsToShow, ...optionalColumnsToShow];
            if (!result.includes('actions')) {
                result = [...result, 'actions'];
            }
            
            Logger.debug("DataTableWithStaticSchema: Column preferences enabled", {
                mandatoryColumns,
                optionalColumns,
                mandatoryColumnsToShow,
                optionalColumnsToShow,
                result
            });
            
            return result;
        }

        // Default behavior: show all available columns and include a single actions column
        const result = availableColumns.includes('actions') ? availableColumns : availableColumns.concat('actions');
        
        Logger.debug("DataTableWithStaticSchema: Default column behavior", {
            availableColumns,
            result
        });
        
        return result;
    }, [schemaObject, rawData, columnsToExclude, enableColumnPreferences, dataSetName, columnPreferences]);

    // Function to format column names for display
    const formatColumnName = (column: string): string => {
        if (column === 'actions') return 'Actions';
        return column
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, str => str.toUpperCase())
            .trim();
    };

    // Build dictionary key for a given column (camelCase -> kebab-case)
    const getDictionaryKeyForColumn = (column: string): string => {
        const kebab = column
            .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
            .replace(/_/g, '-')
            .toLowerCase();
        return `modules-instantupdate-runner-table-column-${kebab}`;
    };

    const getColumnLabel = (column: string): string => {
        // Domain-specific override for campaignType column header
        if (column === 'campaignType') {
            try {
                const host = window?.location?.hostname || '';
                if (host.endsWith('instantupdate.ai')) {
                    return 'Notification Type';
                }
                if (host.endsWith('campaignking.ai')) {
                    return 'Campaign Type';
                }
            } catch (_e) {
                // Fall back to dictionary/format if window not available
            }
        }
        const key = getDictionaryKeyForColumn(column);
        return labels?.[key] ?? formatColumnName(column);
    };

    // Handle sorting logic
    const handleSort = (column: string): void => {
        if (column === 'actions') return;

        if (sortColumn === column) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(column);
            setSortOrder('asc');
        }
        setPage(0);
    };

    // Handle global search input change
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        setSearchQuery(e.target.value);
        setPage(0);
    };

    // Handle individual column filter change
    const handleColumnFilterChange = (column: string, value: string): void => {
        setColumnFilters(prevFilters => ({
            ...prevFilters,
            [column]: value,
        }));
        setPage(0);
    };

    // Handle pagination page change
    const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number): void => {
        setPage(newPage);
    };

    // Handle pagination rows per page change
    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const effectiveSearchQuery = externalSearchQuery !== undefined ? externalSearchQuery : searchQuery;

    // Memoized filtered and sorted data
    const filteredAndSortedData = useMemo((): any[] => {
        let currentData = [...rawData];

        if (effectiveSearchQuery) {
            const lowerCaseQuery = effectiveSearchQuery.toLowerCase();
            currentData = currentData.filter((row: any) =>
                displayColumns.some((column: string) => {
                    if (column === 'actions') return false;
                    const rawValue = row[column];
                    const candidates = getFilterableStrings(column, rawValue);
                    const normalizedQuery = column === 'campaignType'
                        ? normalizeForFilter(column, lowerCaseQuery)
                        : lowerCaseQuery;
                    return candidates.some(v => v.includes(normalizedQuery));
                })
            );
        }

        Object.entries(columnFilters).forEach(([column, filterValue]) => {
            if (filterValue && !columnsWithDisabledFilter.includes(column)) {
                const lowerCaseFilterValue = filterValue.toLowerCase();
                currentData = currentData.filter((row: any) => {
                    const rawValue = row[column];
                    const candidates = getFilterableStrings(column, rawValue);
                    const normalizedFilter = column === 'campaignType'
                        ? normalizeForFilter(column, lowerCaseFilterValue)
                        : lowerCaseFilterValue;
                    return candidates.some(v => v.includes(normalizedFilter));
                });
            }
        });

        if (sortColumn && sortColumn !== 'actions') {
            currentData = currentData.sort((a: any, b: any) => {
                if (isDateColumn(sortColumn)) {
                    const ta = new Date(a[sortColumn]).getTime();
                    const tb = new Date(b[sortColumn]).getTime();
                    const aOk = !Number.isNaN(ta);
                    const bOk = !Number.isNaN(tb);
                    if (!aOk && !bOk) return 0;
                    if (!aOk) return 1;
                    if (!bOk) return -1;
                    if (ta < tb) return sortOrder === 'asc' ? -1 : 1;
                    if (ta > tb) return sortOrder === 'asc' ? 1 : -1;
                    return 0;
                }
                const aValue = String(a[sortColumn] || '').toLowerCase();
                const bValue = String(b[sortColumn] || '').toLowerCase();

                if (aValue < bValue) {
                    return sortOrder === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortOrder === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }

        return currentData;
    }, [rawData, effectiveSearchQuery, columnFilters, sortColumn, sortOrder, displayColumns]);

    // Memoized paginated data
    const paginatedData = useMemo((): any[] => {
        const startIndex = page * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        return filteredAndSortedData.slice(startIndex, endIndex);
    }, [filteredAndSortedData, page, rowsPerPage]);

    if (loading) {
        return (
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '200px',
                flexDirection: 'column',
                gap: 2
            }}>
                <CircularProgress color="secondary" size={40} />
                <Typography variant="body2" color="text.secondary">
                    Loading data...
                </Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '256px' }}>
                <Typography color="error" variant="h6">
                    {error}
                </Typography>
            </Box>
        );
    }

    if (!schemaObject || displayColumns.length === 0) {
        return (
            <Box
                sx={{
                    color: 'gray.600',
                    padding: 2,
                    backgroundColor: 'warning.light',
                    border: '1px solid',
                    borderColor: 'warning.main',
                    borderRadius: '8px',
                }}
            >
                No schema or displayable columns found.
            </Box>
        );
    }

    return (
        <Box>
            {/* Column Selector and Global Search */}
            <Box sx={{ 
                display: 'flex', 
                flexDirection: 'row', 
                gap: 2, 
                mb: 2,
                alignItems: 'center',
                justifyContent: 'flex-start'
            }}>
                {/* Column Selector */}
                {enableColumnPreferences && dataSetName && (
                    <Box sx={{ 
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center'
                    }}>
                        <ColumnSelector
                            dataSetName={dataSetName}
                            allSchemaColumns={Object.keys(schemaObject?.paths || {})}
                            allowedOptionalColumns={allowedOptionalColumns}
                            onColumnPreferencesChange={setColumnPreferences}
                            currentPreferences={columnPreferences}
                            mandatoryColumns={defaultMandatoryColumns}
                        />
                    </Box>
                )}
                
                {/* Global Search */}
                {!hideSearch && (
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Search across all columns..."
                        value={searchQuery}
                        onChange={handleSearchChange}
                        sx={{
                            flexGrow: 1,
                            maxWidth: { xs: '100%', md: '50%' },
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '6px',
                                height: '48px',
                            },
                            transition: 'all 0.2s ease-in-out',
                            '& .MuiOutlinedInput-input': {
                                padding: '12px 16px',
                                fontSize: '0.875rem',
                            },
                        }}
                    />
                )}
                {headerRightContent && (
                    <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
                        {headerRightContent}
                    </Box>
                )}
            </Box>

            <TableContainer
                component={Paper}
                sx={{
                    borderRadius: '8px',
                    border: '1px solid',
                    borderColor: theme.palette.divider,
                    overflowX: 'auto',
                }}
            >
                <Table stickyHeader aria-label="Dynamic Data Table">
                    <TableHead>
                        <TableRow>
                            {displayColumns.map((column) => (
                                <TableCell
                                    key={column}
                                    onClick={() => handleSort(column)}
                                    sx={{
                                        cursor: column === 'actions' ? 'default' : 'pointer',
                                        userSelect: 'none',
                                        '&:hover': {
                                            backgroundColor: column === 'actions' ? 'inherit' : theme.palette.background.paper,
                                        },
                                        textTransform: 'uppercase',
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        {getColumnLabel(column)}
                                        {sortColumn === column && column !== 'actions' && (
                                            <Box component="span" sx={{ marginLeft: 1 }}>
                                                {sortOrder === 'asc' ? '▲' : '▼'}
                                            </Box>
                                        )}
                                    </Box>
                                </TableCell>
                            ))}
                        </TableRow>
                        {/* Column Filter Row */}
                        <TableRow>
                            {displayColumns.map((column) => (
                                <TableCell key={`filter-${column}`}>
                                    {column !== 'actions' && !columnsWithDisabledFilter.includes(column) ? (
                                        <TextField
                                            fullWidth
                                            variant="outlined"
                                            size="small"
                                            placeholder={`Filter ${getColumnLabel(column)}`}
                                            value={columnFilters[column] || ''}
                                            onChange={(e) => handleColumnFilterChange(column, e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '4px',
                                                    fontSize: '0.875rem',
                                                },
                                                '& .MuiOutlinedInput-input': {
                                                    padding: '8px 12px',
                                                },
                                            }}
                                        />
                                    ) : (
                                        <Box sx={{ height: '40px' }} />
                                    )}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedData.length > 0 ? (
                            paginatedData.map((row: any) => (
                                <TableRow key={row._id}>
                                    {displayColumns.map((column) => (
                                        <TableCell
                                            key={`${row._id}-${column}`}
                                            sx={{
                                                whiteSpace: 'nowrap',
                                                fontSize: '0.875rem',
                                            }}
                                        >
                                            {column === 'actions' ? (
                                                columnRenderers?.['actions'] ? (
                                                    columnRenderers['actions'](row)
                                                ) : (
                                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                    {onEdit && (
                                                        <Tooltip title="Edit">
                                                            <Button
                                                                color="secondary"
                                                                variant="contained"
                                                                size="small"
                                                                onClick={() => onEdit(row)}
                                                                sx={{
                                                                    borderRadius: 1,
                                                                    textTransform: 'none',
                                                                    px: 1.25,
                                                                    minWidth: 36,
                                                                }}
                                                            >
                                                                <EditIcon fontSize="small" />
                                                            </Button>
                                                        </Tooltip>
                                                    )}
                                                    {onDelete && (
                                                        <Tooltip 
                                                            title={isDeleteDisabled && isDeleteDisabled(row) 
                                                                ? (deleteDisabledTooltip ? deleteDisabledTooltip(row) : 'Cannot delete this item')
                                                                : 'Delete this item'
                                                            }
                                                        >
                                                            <span> {/* Wrapper needed for disabled buttons with tooltip */}
                                                                <Button
                                                                    color="error"
                                                                    variant="contained"
                                                                    size="small"
                                                                    onClick={() => onDelete(row)}
                                                                    disabled={isDeleteDisabled ? isDeleteDisabled(row) : false}
                                                                    sx={{
                                                                        borderRadius: 1,
                                                                        textTransform: 'none',
                                                                        px: 1.25,
                                                                        minWidth: 36,
                                                                    }}
                                                                >
                                                                    <DeleteIcon fontSize="small" />
                                                                </Button>
                                                            </span>
                                                        </Tooltip>
                                                    )}
                                                    {/* Custom Actions */}
                                                    {customActions && customActions(row).map((action: CustomAction, index: number) => (
                                                        <Tooltip key={`${row._id}-custom-${index}`} title={action.label}>
                                                            <Button
                                                                color={action.color || 'primary'}
                                                                variant="contained"
                                                                size="small"
                                                                onClick={() => action.onClick(row)}
                                                                sx={{
                                                                    borderRadius: 1,
                                                                    textTransform: 'none',
                                                                    px: 1.25,
                                                                    minWidth: 36,
                                                                }}
                                                            >
                                                                {action.icon || <LockResetIcon />}
                                                            </Button>
                                                        </Tooltip>
                                                    ))}
                                                </Box>
                                                )
                                            ) : (
                                                columnRenderers?.[column] ? (
                                                    columnRenderers[column](row)
                                                ) : (
                                                    schemaObject.paths?.[column]?.instance === 'Date' && row[column]
                                                        ? new Date(row[column]).toLocaleString('en-GB', {
                                                            year: 'numeric',
                                                            month: '2-digit',
                                                            day: '2-digit',
                                                            hour: '2-digit',
                                                            minute: '2-digit',
                                                            second: '2-digit',
                                                            timeZoneName: 'short'
                                                        })
                                                        : schemaObject.paths?.[column]?.instance === 'Boolean'
                                                        ? (row[column] === true ? 'Yes' : row[column] === false ? 'No' : '')
                                                        : String(row[column] || '')
                                                )
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell
                                    colSpan={displayColumns.length}
                                    sx={{
                                        textAlign: 'center',
                                        padding: 2,
                                    }}
                                >
                                    No matching records found.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Table Pagination */}
            <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={filteredAndSortedData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                    '.MuiTablePagination-toolbar': {
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                    },
                    '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                        marginBottom: { xs: 1, sm: 0 },
                    },
                }}
            />
        </Box>
    );
};
